import { useRef, useState } from 'react';
import { useQueryCall } from '../../../../api/react-client';

export const COPILOT_POLL_STATUS = {
  PENDING: 'PENDING',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
};

const POLL_INTERVAL_MS = 250;
// Backend self-terminates to FAILED ~10s after the triggering call if the
// guidance session never reaches a terminal state, so this is a generous
// frontend backstop, not the expected path - it only kicks in if something
// breaks the normal PENDING -> COMPLETED/FAILED contract (e.g. transport
// errors that keep failing, or a malformed response stuck on PENDING).
const MAX_POLL_ATTEMPTS = 240; // ~60s at 250ms

/**
 * Shared polling behavior for `identify_complaint`/`verify_complaint` -
 * structurally identical "PENDING keeps polling, COMPLETED/FAILED are
 * terminal" contracts. `retry: false` plus stopping outright on error keep
 * a broken transport from retrying forever, since no terminal status would
 * ever naturally arrive on its own.
 */
export function useCopilotPolling({ enabled, queryKey, queryFn }) {
  const [stopped, setStopped] = useState(false);
  const attemptsRef = useRef(0);
  const queryKeyRef = useRef(queryKey);

  // A changed queryKey means the caller redid the step that feeds this poll
  // (e.g. re-confirming step 1 with a different selection) - react-query
  // already treats that as a brand new query, but `stopped`/`attemptsRef`
  // live on this hook's own call site, not on the key, so they need
  // resetting by hand or a prior cycle's backstop would leak into the new
  // one. Safe to adjust during render: it's a plain prop-vs-ref comparison
  // that bails out once the refs catch up.
  const serializedKey = JSON.stringify(queryKey);
  if (JSON.stringify(queryKeyRef.current) !== serializedKey) {
    queryKeyRef.current = queryKey;
    attemptsRef.current = 0;
    if (stopped) setStopped(false);
  }

  const { data: res, isError } = useQueryCall(
    {
      enabled: Boolean(enabled && !stopped),
      retry: false,
      refetchInterval: (latest) => {
        if (!latest || latest?.status === COPILOT_POLL_STATUS.PENDING) {
          attemptsRef.current += 1;
          if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
            setStopped(true);
            return false;
          }
          return POLL_INTERVAL_MS;
        }
        return false;
      },
      onError: () => setStopped(true),
    },
    null,
    queryKey,
    queryFn,
  );

  // Trust the response body's own `status` whenever one has actually
  // arrived, rather than react-query's `isLoading` flag - `isLoading` only
  // reflects the very first fetch (`status === 'loading'` in react-query's
  // own terms), so it's an indirect proxy for "do we have fresh data" that
  // adds a layer of timing assumptions this doesn't need.
  const status = res?.status
    ? res.status
    : isError || stopped
    ? COPILOT_POLL_STATUS.FAILED
    : COPILOT_POLL_STATUS.PENDING;

  return { data: res?.data, status };
}
